import MenuField from "./MenuField";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { selectUser } from "../../redux/main/authSlice";
import { selectColorsSchema } from "../../redux/main/uiSlice";
import { selectSidebarIsClose } from "../../redux/sidebarSlice";

export default function Menu({ modules = [] }) {
    // Recupero i dati dell'utente dal Redux store
    const user = useSelector(selectUser);
    const isCustomerUser = user && user.customerUserId;
    // Recupero il roleId dell'utente se presente
    const userRoleId = user?.roleId;
    const colorsSchema = useSelector(selectColorsSchema);
    const isClose = useSelector(selectSidebarIsClose);
    
    // Verifica che modules sia un array valido
    if (!Array.isArray(modules)) {
        console.warn('Menu: modules non è un array valido', modules);
        return (
            <div className="px-4 py-2 text-sm text-gray-500">
                Menu non disponibile
            </div>
        );
    }

    // Filtra i moduli in base al roleId dell'utente (enableForUserType)
    const filteredModules = modules.filter(module => {
        // Se il modulo non ha enableForUserType, lo mostro per retrocompatibilità
        if (!module.enableForUserType || !Array.isArray(module.enableForUserType)) {
            return true;
        }
        
        // Se l'utente è un customer, controllo se il modulo è abilitato per i customer
        if (isCustomerUser) {
            return module.enableForUserType.includes("customer");
        }
        
        // Per utenti con userId, verifico se roleId è nella lista dei ruoli abilitati
        if (userRoleId) {
            return module.enableForUserType.includes(userRoleId);
        }
        
        // Se non ho roleId ma ho userId, mostro comunque per retrocompatibilità
        return !isCustomerUser;
    });

    // In questo componente devo creare le gerarchie delle voci di menu padre/figlio, perché i figli non sono contenuti nei moduli dei padri
    // Creo l'array di moduli padre, eliminando anche le voci che non hanno la condizione sidebar
    const fatherModules = filteredModules.filter(module => module && module.father && module.sideBar).map(fatherModule => ({ ...fatherModule, sons: [] }));
    
    // Divido gli elementi in tre categorie:
    // 1. Elementi padre (con father=true)
    // 2. Elementi figlio (con father=false e fatherId valorizzato)
    // 3. Elementi orfani (con father=false e senza fatherId)
    
    // Per gli elementi figlio, li associo ai padri corrispondenti
    filteredModules
        .filter(module => module && !module.father && module.sideBar && module.fatherId)
        .forEach(sonModule => {
            const matchFather = fatherModules.find(father => father.id === sonModule.fatherId);
            if (matchFather) matchFather.sons.push(sonModule);
        });
        
    // Gli elementi orfani li tratto come elementi padre ma con sons vuoto
    const orphanModules = filteredModules.filter(module => 
        module && 
        !module.father && 
        module.sideBar && 
        (!module.fatherId || module.fatherId === '')
    );
    
    // Aggiungo gli elementi orfani all'array dei padri
    // Assicurandomi che abbiano tutte le proprietà necessarie per funzionare come elementi navigabili
    orphanModules.forEach(module => {
        // Creo una struttura dati compatibile con ciò che si aspetta MenuField
        // ma con un comportamento simile ai submenuField per la navigazione
        const adaptedModule = {
            ...module,
            father: true,
            sons: [],
            // Assicuriamoci che ci sia sempre un ID
            id: module.id || `orphan-${Math.random().toString(36).substring(2, 9)}`,
            // Assicuriamoci che ci sia menuLabel per la visualizzazione
            menuLabel: module.menuLabel || [{ label: module.label || module.name || 'Menu Item' }],
            // Se path non è specificato, utilizziamo l'ID come path (come per SubMenuField)
            path: module.path || `/${module.id}`,
            // Aggiungiamo una proprietà per identificare gli elementi orfani
            isOrphan: true
        };
        
        fatherModules.push(adaptedModule);
    });

    return (
        <div className="flex flex-col">
            {/* Renderizza tutti i moduli (inclusi gli orfani che sono stati aggiunti come padri senza figli) */}
            {
                fatherModules?.map((element, index) => {
                    return <MenuField key={index} module={element} />
                })
            }
        </div>
    )
}
